import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Shield, User as UserIcon } from 'lucide-react';
import type { User } from '../types/User';
import { createUser } from '../types/User';
interface UserManagementProps {
  onBack: () => void;
}
export function UserManagement({
  onBack
}: UserManagementProps) {
  const [users, setUsers] = useState<User[]>([{
    id: '1',
    name: 'Max Mustermann',
    personalNumber: 'P-001',
    rfidChip: 'RFID-001',
    isAdmin: true,
    createdAt: new Date('2024-01-15')
  }, {
    id: '2',
    name: 'Anna Schmidt',
    personalNumber: 'P-002',
    rfidChip: 'RFID-002',
    isAdmin: false,
    createdAt: new Date('2024-02-20')
  }]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    personalNumber: '',
    rfidChip: '',
    isAdmin: false
  });
  const resetForm = () => {
    setFormData({
      name: '',
      personalNumber: '',
      rfidChip: '',
      isAdmin: false
    });
  };
  const handleAddUser = () => {
    if (formData.name && formData.personalNumber && formData.rfidChip) {
      const newUser = createUser(formData.name, formData.personalNumber, formData.rfidChip, formData.isAdmin);
      setUsers([...users, newUser]);
      setShowAddModal(false);
      resetForm();
    }
  };
  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      personalNumber: user.personalNumber,
      rfidChip: user.rfidChip,
      isAdmin: user.isAdmin
    });
    setShowEditModal(true);
  };
  const handleUpdateUser = () => {
    if (selectedUser && formData.name && formData.personalNumber && formData.rfidChip) {
      setUsers(users.map(u => u.id === selectedUser.id ? {
        ...u,
        name: formData.name,
        personalNumber: formData.personalNumber,
        rfidChip: formData.rfidChip,
        isAdmin: formData.isAdmin
      } : u));
      setShowEditModal(false);
      setSelectedUser(null);
      resetForm();
    }
  };
  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setShowDeleteConfirm(true);
  };
  const handleConfirmDelete = () => {
    if (selectedUser) {
      setUsers(users.filter(u => u.id !== selectedUser.id));
      setShowDeleteConfirm(false);
      setSelectedUser(null);
    }
  };
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setSelectedUser(null);
  };
  return <div className="space-y-6" data-id="element-803">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-804">
        <h2 className="text-2xl font-semibold text-gray-900" data-id="element-805">
          Benutzerverwaltung
        </h2>
        <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors" data-id="element-806">
          <Plus className="w-4 h-4" strokeWidth={2} data-id="element-807" />
          Benutzer hinzufügen
        </button>
      </div>

      {/* Users List */}
      <div className="space-y-3" data-id="element-808">
        {users.length === 0 ? <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200" data-id="element-809">
            <UserIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" strokeWidth={1.5} data-id="element-810" />
            <p className="text-gray-500" data-id="element-811">Noch keine Benutzer angelegt</p>
          </div> : users.map(user => <motion.div key={user.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-id="element-812">
              <div className="flex items-center justify-between" data-id="element-813">
                <div className="flex items-center gap-4" data-id="element-814">
                  <div className={`p-3 rounded-lg ${user.isAdmin ? 'bg-purple-100' : 'bg-gray-100'}`} data-id="element-815">
                    {user.isAdmin ? <Shield className="w-5 h-5 text-purple-600" strokeWidth={2} data-id="element-816" /> : <UserIcon className="w-5 h-5 text-gray-600" strokeWidth={2} data-id="element-817" />}
                  </div>
                  <div data-id="element-818">
                    <div className="flex items-center gap-2" data-id="element-819">
                      <h3 className="font-semibold text-gray-900" data-id="element-820">
                        {user.name}
                      </h3>
                      {user.isAdmin && <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full" data-id="element-821">
                          Admin
                        </span>}
                    </div>
                    <p className="text-sm text-gray-500" data-id="element-822">
                      Personal-Nr: {user.personalNumber} • RFID: {user.rfidChip}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2" data-id="element-823">
                  <button onClick={() => handleEditClick(user)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Bearbeiten" data-id="element-824">
                    <Edit2 className="w-4 h-4" strokeWidth={2} data-id="element-825" />
                  </button>
                  <button onClick={() => handleDeleteClick(user)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" aria-label="Löschen" data-id="element-826">
                    <Trash2 className="w-4 h-4" strokeWidth={2} data-id="element-827" />
                  </button>
                </div>
              </div>
            </motion.div>)}
      </div>

      {/* Add User Modal */}
      <AnimatePresence data-id="element-828">
        {showAddModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-[60]" onClick={() => {
          setShowAddModal(false);
          resetForm();
        }} data-id="element-829" />
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-830">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} className="w-full max-w-md" data-id="element-831">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-id="element-832">
                  <div className="p-6" data-id="element-833">
                    <div className="flex justify-between items-start mb-6" data-id="element-834">
                      <h3 className="text-2xl font-semibold text-gray-900" data-id="element-835">
                        Benutzer hinzufügen
                      </h3>
                      <button onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }} className="p-1 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-836">
                        <X className="w-5 h-5 text-gray-500" strokeWidth={2} data-id="element-837" />
                      </button>
                    </div>

                    <div className="space-y-4" data-id="element-838">
                      <div data-id="element-839">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-840">
                          Name
                        </label>
                        <input type="text" value={formData.name} onChange={e => setFormData({
                      ...formData,
                      name: e.target.value
                    })} placeholder="Max Mustermann" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-841" />
                      </div>

                      <div data-id="element-842">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-843">
                          Personalnummer
                        </label>
                        <input type="text" value={formData.personalNumber} onChange={e => setFormData({
                      ...formData,
                      personalNumber: e.target.value
                    })} placeholder="P-001" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-844" />
                      </div>

                      <div data-id="element-845">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-846">
                          RFID Chip
                        </label>
                        <input type="text" value={formData.rfidChip} onChange={e => setFormData({
                      ...formData,
                      rfidChip: e.target.value
                    })} placeholder="RFID-001" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-847" />
                      </div>

                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-848">
                        <input type="checkbox" id="isAdmin" checked={formData.isAdmin} onChange={e => setFormData({
                      ...formData,
                      isAdmin: e.target.checked
                    })} className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500" data-id="element-849" />
                        <label htmlFor="isAdmin" className="text-sm font-medium text-gray-700 cursor-pointer" data-id="element-850">
                          Administrator-Rechte
                        </label>
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6" data-id="element-851">
                      <button onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }} className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors" data-id="element-852">
                        Abbrechen
                      </button>
                      <button onClick={handleAddUser} disabled={!formData.name || !formData.personalNumber || !formData.rfidChip} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-853">
                        Hinzufügen
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      {/* Edit User Modal */}
      <AnimatePresence data-id="element-854">
        {showEditModal && selectedUser && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-[60]" onClick={() => {
          setShowEditModal(false);
          setSelectedUser(null);
          resetForm();
        }} data-id="element-855" />
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-856">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} className="w-full max-w-md" data-id="element-857">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-id="element-858">
                  <div className="p-6" data-id="element-859">
                    <div className="flex justify-between items-start mb-6" data-id="element-860">
                      <h3 className="text-2xl font-semibold text-gray-900" data-id="element-861">
                        Benutzer bearbeiten
                      </h3>
                      <button onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                    resetForm();
                  }} className="p-1 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-862">
                        <X className="w-5 h-5 text-gray-500" strokeWidth={2} data-id="element-863" />
                      </button>
                    </div>

                    <div className="space-y-4" data-id="element-864">
                      <div data-id="element-865">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-866">
                          Name
                        </label>
                        <input type="text" value={formData.name} onChange={e => setFormData({
                      ...formData,
                      name: e.target.value
                    })} placeholder="Max Mustermann" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-867" />
                      </div>

                      <div data-id="element-868">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-869">
                          Personalnummer
                        </label>
                        <input type="text" value={formData.personalNumber} onChange={e => setFormData({
                      ...formData,
                      personalNumber: e.target.value
                    })} placeholder="P-001" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-870" />
                      </div>

                      <div data-id="element-871">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-872">
                          RFID Chip
                        </label>
                        <input type="text" value={formData.rfidChip} onChange={e => setFormData({
                      ...formData,
                      rfidChip: e.target.value
                    })} placeholder="RFID-001" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-873" />
                      </div>

                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-874">
                        <input type="checkbox" id="isAdminEdit" checked={formData.isAdmin} onChange={e => setFormData({
                      ...formData,
                      isAdmin: e.target.checked
                    })} className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500" data-id="element-875" />
                        <label htmlFor="isAdminEdit" className="text-sm font-medium text-gray-700 cursor-pointer" data-id="element-876">
                          Administrator-Rechte
                        </label>
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6" data-id="element-877">
                      <button onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                    resetForm();
                  }} className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors" data-id="element-878">
                        Abbrechen
                      </button>
                      <button onClick={handleUpdateUser} disabled={!formData.name || !formData.personalNumber || !formData.rfidChip} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-879">
                        Speichern
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence data-id="element-880">
        {showDeleteConfirm && selectedUser && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-[60]" onClick={handleCancelDelete} data-id="element-881" />
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-882">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} className="w-full max-w-md" data-id="element-883">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-id="element-884">
                  <div className="p-6" data-id="element-885">
                    <div className="flex justify-between items-start mb-4" data-id="element-886">
                      <h3 className="text-2xl font-semibold text-gray-900" data-id="element-887">
                        Benutzer löschen?
                      </h3>
                      <button onClick={handleCancelDelete} className="p-1 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-888">
                        <X className="w-5 h-5 text-gray-500" strokeWidth={2} data-id="element-889" />
                      </button>
                    </div>

                    <p className="text-gray-600 mb-6" data-id="element-890">
                      Möchten Sie den Benutzer{' '}
                      <strong data-id="element-891">{selectedUser.name}</strong> wirklich löschen?
                    </p>

                    <div className="flex gap-3" data-id="element-892">
                      <button onClick={handleCancelDelete} className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors" data-id="element-893">
                        Abbrechen
                      </button>
                      <button onClick={handleConfirmDelete} className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors" data-id="element-894">
                        Löschen
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>
    </div>;
}